import { FormControl, FormHelperText, InputLabel, MenuItem, OutlinedInput, Select } from '@material-ui/core';
import { withOktaAuth } from '@okta/okta-react';
import { IOktaContext } from '@okta/okta-react/bundles/types/OktaContext';
import { useAccessibilityContext } from 'pages/Unitlookup/stores/AccessibilityProvider/AccessibilityProvider';
import { Gateway, operatingModeType, OperatingModeType } from 'pages/Unitlookup/stores/TelematicsDevice/DeviceStateApi';
import { useDeviceContext } from 'pages/Unitlookup/stores/TelematicsDevice/TelematicsDeviceProvider';
import { isFirmwareGreaterThanOrEqualTo } from 'pages/Unitlookup/stores/TelematicsDevice/FirmwareComparison';
import React from 'react';
import { StateStatus } from '../DeviceState/StateStatus';

interface ModeOption {
  value: OperatingModeType;
  label: string;
}

const TU600_MODES: ModeOption[] = [
  { value: operatingModeType.NORMAL, label: 'Normal' },
  { value: operatingModeType.STOCK, label: 'Stock' },
  { value: operatingModeType.TRANSPORT, label: 'Transport' },
];

const TU700_MODES: ModeOption[] = [
  ...TU600_MODES,
  { value: operatingModeType.RESTRICTED, label: 'Restricted' },
];

/** TU700 modes available from firmware 2.5.12 onwards */
const TU700_MODES_FROM_FW_2_5_12: ModeOption[] = [
  ...TU700_MODES,
  { value: operatingModeType.BATTERY_GUARD, label: 'Battery Guard' },
  { value: operatingModeType.HIGH_AVAILABILITY, label: 'High Availability' },
];

const OperatingMode: React.FC<IOktaContext> = () => {
  const { accessibility } = useAccessibilityContext();
  const { state, identity, operatingMode, setOperatingMode, isGriffinDevice } = useDeviceContext();

  const stateMode = state.desired.operatingMode?.mode;
  const firmwareVersion = state.reported?.firmware?.version;
  
  const getAvailableModes = (): ModeOption[] => {
    if (!isGriffinDevice(identity)) {
      return TU600_MODES;
    }
    if (isFirmwareGreaterThanOrEqualTo(firmwareVersion, 2, 5, 12)) {
      return TU700_MODES_FROM_FW_2_5_12;
    }
    return TU700_MODES;
  };

  const getReadonlyExplanation = (): string | undefined => {
    if (!accessibility?.operatingMode.readonly) {
      return undefined;
    }
    switch (accessibility.operatingMode.accessReason) {
      case 'NOT_OWNER_ACCOUNT':
        return 'Only Fleet Admins may change the Operating Mode';
      case 'FIRMWARE_NOT_SUPPORTED':
        return 'A firmware version of 63.08 or higher is required to change the Operating Mode';
      default:
        return undefined;
    }
  };

  const getModeSpecificHelperText = (): string | undefined => {
    if (operatingMode === operatingModeType.HIGH_AVAILABILITY) {
      return 'Applies to Machines that has no ignition wired (INPUT 2 not wired)';
    }
    return undefined;
  };

  const helperText = getReadonlyExplanation() ?? getModeSpecificHelperText();
  const currentValue = operatingMode ?? stateMode ?? operatingModeType.NORMAL;

  return (
    <div className="stateWrapper">
      <FormControl
        variant="outlined"
        fullWidth
        margin="normal"
        disabled={accessibility?.operatingMode.readonly}
      >
        <InputLabel htmlFor="operating-mode">Operating Mode</InputLabel>
        <Select
          value={currentValue}
          input={
            <OutlinedInput
              labelWidth={120}
              name="operatingMode"
              id="operating-mode"
            />
          }
          onChange={(evt: any) => setOperatingMode(evt.target.value)}
        >
          {getAvailableModes().map(mode => (
            <MenuItem key={mode.value} value={mode.value}>
              {mode.label}
            </MenuItem>
          ))}
        </Select>
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
      <StateStatus
          getIdentityFromDesiredState={() => state.desired.operatingMode?.mode?.toString()}
          getIdentityFromReportedState={() => state.reported.operatingMode?.mode?.toString()}
          getGatewayUsedFromDesiredMetadataState={() => Gateway.IRIS}
      />
    </div>
  );
};

export default withOktaAuth(OperatingMode);
